import React, { useEffect, useRef, useState } from "react";
import { FRAG, VERT } from "./FieldShader";
import { field } from "../../lib/field";
import { prefersReducedMotion } from "../../lib/anime";
import FieldFallback from "./FieldFallback";

const DPR_CAP = 1.5;

/** '#38D9C4' → [0.22, 0.85, 0.77] */
const hexToVec3 = (hex) => {
  const h = hex.trim().replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const int = parseInt(full, 16);
  return [((int >> 16) & 255) / 255, ((int >> 8) & 255) / 255, (int & 255) / 255];
};

const readToken = (name, fallback) => {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name);
  return hexToVec3(value || fallback);
};

const compile = (gl, type, source) => {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Field shader failed to compile: ${log}`);
  }
  return shader;
};

/**
 * The living ground of the dark chapters: one quad, one fragment shader, no 3D
 * library and no added dependency.
 *
 * Mounted inside a chapter rather than fixed behind the whole page, because the
 * sheet is opaque by construction — a global layer would have nowhere to show.
 * It sizes to its own box, idles while off screen, and paints a single frame
 * under reduced motion.
 */
const FieldCanvas = ({ className = "field-layer" }) => {
  const canvasRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const gl = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
    });

    if (!gl || gl.isContextLost()) {
      setFailed(true);
      return undefined;
    }

    /* A real GPU reset drops us to the CSS fallback rather than a black page */
    const onContextLost = (event) => {
      event.preventDefault();
      setFailed(true);
    };
    canvas.addEventListener("webglcontextlost", onContextLost);

    let program;
    try {
      const vs = compile(gl, gl.VERTEX_SHADER, VERT);
      const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
      program = gl.createProgram();
      gl.attachShader(program, vs);
      gl.attachShader(program, fs);
      gl.bindAttribLocation(program, 0, "aPos");
      gl.linkProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(error);
      setFailed(true);
      return undefined;
    }

    gl.useProgram(program);

    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW
    );
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const u = (name) => gl.getUniformLocation(program, name);
    const loc = {
      res: u("uRes"),
      time: u("uTime"),
      mouse: u("uMouse"),
      warp: u("uWarp"),
      hue: u("uHue"),
      bloom: u("uBloom"),
      lift: u("uLift"),
      opacity: u("uOpacity"),
    };

    /* Palette comes straight from the token layer, so the shader can never
       drift away from the CSS. */
    const paintPalette = () => {
      gl.uniform3fv(u("uAbyss"), readToken("--abyss", "#0A1417"));
      gl.uniform3fv(u("uDeep"), readToken("--deep", "#143039"));
      gl.uniform3fv(u("uCyan"), readToken("--cyan", "#38D9C4"));
      gl.uniform3fv(u("uPeach"), readToken("--peach", "#F7B08A"));
      gl.uniform3fv(u("uAmber"), readToken("--amber", "#E8A23C"));
      gl.uniform3fv(u("uIris"), readToken("--iris", "#6E7FE8"));
    };
    paintPalette();

    let width = 0;
    let height = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
      const box = canvas.getBoundingClientRect();
      const w = Math.round(Math.max(box.width, 1) * dpr);
      const h = Math.round(Math.max(box.height, 1) * dpr);
      if (w === width && h === height) return;
      width = w;
      height = h;
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(loc.res, w, h);
    };
    resize();

    const draw = (seconds) => {
      gl.uniform1f(loc.time, seconds);
      gl.uniform2f(loc.mouse, field.mx, field.my);
      gl.uniform1f(loc.warp, field.warp);
      gl.uniform1f(loc.hue, field.hue);
      gl.uniform1f(loc.bloom, field.bloom);
      gl.uniform1f(loc.lift, field.lift);
      gl.uniform1f(loc.opacity, field.opacity);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const reduced = prefersReducedMotion();

    if (reduced) {
      /* One still frame, fully bloomed, no loop at all. */
      field.opacity = 1;
      draw(12);
      const onResizeStill = () => {
        resize();
        draw(12);
      };
      window.addEventListener("resize", onResizeStill);
      return () => {
        window.removeEventListener("resize", onResizeStill);
        canvas.removeEventListener("webglcontextlost", onContextLost);
        gl.deleteProgram(program);
        gl.deleteBuffer(buffer);
        gl.deleteVertexArray(vao);
      };
    }

    let raf = 0;
    let last = performance.now();
    let onScreen = true;

    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        if (onScreen) last = performance.now();
      },
      { rootMargin: "20%" }
    );
    io.observe(canvas);

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      /* Pointer damping — frame-rate independent exponential approach */
      const k = 1 - Math.exp(-2.6 * dt);
      field.mx += (field.targetX - field.mx) * k;
      field.my += (field.targetY - field.my) * k;

      /* Idle while this chapter is off screen */
      if (!onScreen) return;

      field.time += dt;
      resize();
      draw(field.time);
    };
    raf = requestAnimationFrame(frame);

    const onPointerMove = (event) => {
      const box = canvas.getBoundingClientRect();
      field.targetX = (event.clientX - box.left) / Math.max(box.width, 1);
      field.targetY = (event.clientY - box.top) / Math.max(box.height, 1);
    };

    const onVisibility = () => {
      if (!document.hidden) last = performance.now();
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("visibilitychange", onVisibility);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      /* Deliberately no loseContext(): StrictMode re-runs this effect on the
         same canvas, and a canvas whose context has been lost can never return
         a working one again. The context dies with the element. */
      gl.deleteProgram(program);
      gl.deleteBuffer(buffer);
      gl.deleteVertexArray(vao);
    };
  }, []);

  if (failed) return <FieldFallback className={className} />;

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
};

export default FieldCanvas;
